import {
  ReactNode,
  RefObject,
  InputHTMLAttributes,
  useCallback,
  useState,
  useId,
  forwardRef,
  useRef,
} from 'react';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  InputStyleProps,
  InputWrapper,
  PresetIcon,
  FormErrorIcon,
  FormWarningIcon,
} from '../../atoms';
import { Field } from '../Field/Field';
import { CommonFieldProps } from '../Field/types';
import {
  IconButtonInInput,
  PostfixMsg,
  PrefixMsg,
  TextInputStyle,
} from './atoms';
import { getChildElementId } from '../../utils';
import { FormSize } from '../../types';

type RightIconProps = {
  iconName?: IconName;
  onClick?: VoidFunction;
  tooltip?: string;
  isDisabled?: boolean;
  showClearIcon?: boolean;
  onClear?: VoidFunction;
  fieldName?: string;
  i18nLocale: I18nLocale;
};

const getClearButtonAriaLabel = (
  fieldName: string | undefined,
  i18nLocale: I18nLocale
): string =>
  fieldName
    ? t(
        i18nLocale,
        'forms:textInput.clearFieldAriaLabel',
        { fieldName },
        `Clear ${fieldName}`
      )
    : t(i18nLocale, 'forms:textInput.clearInputAriaLabel', 'Clear input');

const RightIcon = ({
  iconName,
  onClick,
  tooltip,
  isDisabled,
  showClearIcon,
  onClear,
  fieldName,
  i18nLocale,
}: RightIconProps) => {
  if (showClearIcon) {
    return (
      <IconButtonInInput
        type="button"
        onClick={isDisabled ? undefined : onClear}
        aria-disabled={isDisabled}
        aria-label={getClearButtonAriaLabel(fieldName, i18nLocale)}
        data-tooltip-text={t(
          i18nLocale,
          'forms:textInput.clearTooltip',
          'Clear'
        )}
      >
        <Icon iconName={IconName.CLOSE} iconSize={IconSize.MEDIUM} />
      </IconButtonInInput>
    );
  }

  if (iconName && onClick)
    return (
      <IconButtonInInput
        type="button"
        onClick={isDisabled ? undefined : onClick}
        aria-disabled={isDisabled}
        aria-label={tooltip}
        data-tooltip-text={tooltip}
      >
        <Icon iconName={iconName} iconSize={IconSize.MEDIUM} />
      </IconButtonInInput>
    );

  if (iconName)
    return (
      <PresetIcon
        iconName={iconName}
        $isDisabled={isDisabled}
        data-tooltip-text={tooltip}
        iconSize={IconSize.MEDIUM}
      />
    );

  return null;
};

const toAriaDescribedBy = (
  id: string,
  helperText: ReactNode,
  hintMessage: ReactNode
): string | undefined => {
  const describedByIds = [
    ...(helperText ? [getChildElementId(id, 'help-text')] : []),
    ...(hintMessage ? [getChildElementId(id, 'hint')] : []),
  ];
  if (describedByIds.length === 0) return undefined;
  return describedByIds.join(' ');
};

export type TextInputProps = Omit<CommonFieldProps, 'formSize'> &
  Pick<
    InputHTMLAttributes<HTMLInputElement>,
    | 'onClick'
    | 'onBlur'
    | 'onChange'
    | 'onKeyDown'
    | 'value'
    | 'placeholder'
    | 'defaultValue'
    | 'maxLength'
    | 'name'
    | 'type'
    | 'form'
    | 'autoComplete'
    | 'autoFocus'
    | 'onFocus'
    | 'className'
    | 'tabIndex'
    | 'aria-label'
    | 'aria-errormessage'
  > & {
    formSize?: FormSize | 'fitText';
    leftIconName?: IconName;
    rightIconName?: IconName;
    rightIconOnClick?: VoidFunction;
    rightIconTooltip?: string;
    isReadOnly?: boolean;
    hideErrorIcon?: boolean;
    hideWarningIcon?: boolean;
    alignInputText?: 'left' | 'center' | 'right';
    ref?: RefObject<HTMLInputElement>;
    displayValue?: string;
    id?: string;
    prefix?: string;
    prefixNode?: ReactNode;
    postfix?: string;
    postfixNode?: ReactNode;
    onClear?: VoidFunction;
    /**
     * This will be called when the input is blurred and the value has changed.
     */
    onCommit?: (value: string) => void;
    onValueChange?: (value: string) => void;
    hasTransparentBackground?: boolean;
    hasRightPadding?: boolean;
    hasClearButton?: boolean;
    dataTourId?: string;
    /**
     * Optional locale for translating the package's internal clear-button
     * `aria-label` and tooltip. When omitted, falls back to English so callers
     * that have not yet adopted the i18n flow remain unchanged.
     */
    i18nLocale?: I18nLocale;
  };

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      leftIconName,
      rightIconName,
      rightIconOnClick,
      rightIconTooltip,
      onValueChange,
      errorMessage,
      warningMessage,
      helperText,
      hintMessage,
      isDisabled,
      isReadOnly,
      label,
      value,
      hideErrorIcon,
      hideWarningIcon,
      alignInputText = 'left',
      displayValue,
      indicator,
      formSize,
      prefix,
      prefixNode,
      postfix,
      postfixNode,
      onCloseFieldClick,
      closeFieldIconTooltip,
      __DS_ONLY_fieldStyle,
      dataTestId,
      dataTourId,
      popoverContent,
      popoverHelpContent,
      hasError: hasErrorProp,
      hasWarning: hasWarningProp,
      'aria-errormessage': ariaErrorMessage,
      className,
      onClear,
      onCommit,
      hasTransparentBackground = false,
      hasRightPadding = false,
      hasClearButton = false,
      i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
      isRequired,
      inputId,
      labelId,
      autoComplete = 'off',
      ...props
    },
    ref
  ) => {
    const [isDisplayMode, setDisplayMode] = useState(Boolean(displayValue));

    const isCommitedRef = useRef(false);
    const fitsText = formSize === 'fitText';

    const generatedTextInputId = useId();
    const id = props.id ?? inputId ?? generatedTextInputId;

    const hasError = hasErrorProp || Boolean(errorMessage);
    const errorId = ariaErrorMessage ?? getChildElementId(id, 'error');
    const warningId = getChildElementId(id, 'warning');
    const hasWarning = !hasError && (hasWarningProp || Boolean(warningMessage));
    const hasPrefix = Boolean(prefix || prefixNode);
    const hasPostfix = Boolean(postfix || postfixNode);
    const showClearIcon = Boolean(value) && Boolean(onClear);

    const styleProps: InputStyleProps = {
      $isDisabled: isDisabled || isReadOnly,
      $hasError: hasError,
      $hasWarning: hasWarning,
      $hasLeftIcon: Boolean(leftIconName),
      $hasRightIcon: Boolean(rightIconName || showClearIcon || hasRightPadding),
      $alignText: alignInputText,
      $hideErrorIcon: hideErrorIcon,
      $hideWarningIcon: hideWarningIcon,
      $hasPrefix: hasPrefix,
      $hasPostfix: hasPostfix,
      $hasTransparentBackground: hasTransparentBackground,
      $hasClearButton: hasClearButton,
    };

    const handleChange = useCallback(
      (newValue: string) => {
        onValueChange?.(newValue);
        isCommitedRef.current = true;
      },
      [onValueChange]
    );

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      props.onBlur?.(event);
      if (isCommitedRef.current && onCommit) {
        onCommit(event.target.value);
        isCommitedRef.current = false;
      }

      if (displayValue === undefined) {
        return;
      }

      setDisplayMode(true);
    };

    return (
      <Field
        label={label}
        labelId={labelId}
        inputId={id}
        errorId={errorId}
        warningId={warningId}
        indicator={indicator}
        helperText={helperText}
        isDisabled={isDisabled}
        formSize={fitsText ? undefined : formSize}
        onCloseFieldClick={onCloseFieldClick}
        closeFieldIconTooltip={closeFieldIconTooltip}
        hintMessage={hintMessage}
        errorMessage={errorMessage}
        i18nLocale={i18nLocale}
        warningMessage={warningMessage}
        __DS_ONLY_fieldStyle={
          fitsText
            ? {
                ...__DS_ONLY_fieldStyle,
                width: 'fit-content',
                minWidth: 0,
                maxWidth: '100%',
              }
            : __DS_ONLY_fieldStyle
        }
        dataTestId={dataTestId}
        popoverContent={popoverContent}
        popoverHelpContent={popoverHelpContent}
        className={className}
      >
        <InputWrapper {...styleProps} data-tour-id={dataTourId}>
          {prefixNode}
          {prefix && <PrefixMsg {...styleProps}>{prefix}</PrefixMsg>}
          {leftIconName && (
            <PresetIcon
              {...styleProps}
              iconName={leftIconName}
              iconSize={IconSize.MEDIUM}
            />
          )}
          {isDisplayMode ? (
            <TextInputStyle
              id={id}
              ref={ref}
              {...props}
              autoComplete={autoComplete}
              {...styleProps}
              $fitsText={fitsText}
              value={displayValue}
              readOnly={isReadOnly}
              disabled={isDisabled}
              onChange={() => {}}
              onFocus={() => setDisplayMode(false)}
            />
          ) : (
            <TextInputStyle
              {...props}
              autoComplete={autoComplete}
              {...styleProps}
              $fitsText={fitsText}
              id={id}
              ref={ref}
              value={value}
              readOnly={isReadOnly}
              disabled={isDisabled}
              aria-describedby={toAriaDescribedBy(id, helperText, hintMessage)}
              aria-errormessage={hasError ? errorId : undefined}
              aria-invalid={hasError ? 'true' : undefined}
              aria-required={
                isRequired || indicator === 'required' ? 'true' : undefined
              }
              onChange={e => {
                const newValue = e.currentTarget.value;
                handleChange(newValue);
                props.onChange?.(e);
              }}
              onBlur={handleBlur}
            />
          )}

          {!showClearIcon && hasError && !hideErrorIcon && (
            <FormErrorIcon iconSize={IconSize.MEDIUM} />
          )}
          {!showClearIcon && !hasError && hasWarning && !hideWarningIcon && (
            <FormWarningIcon iconSize={IconSize.MEDIUM} />
          )}
          <RightIcon
            iconName={rightIconName}
            onClick={rightIconOnClick}
            tooltip={rightIconTooltip}
            showClearIcon={showClearIcon}
            isDisabled={isDisabled}
            onClear={onClear}
            fieldName={label ?? props.placeholder}
            i18nLocale={i18nLocale}
          />
          {postfix && <PostfixMsg {...styleProps}>{postfix}</PostfixMsg>}
          {postfixNode}
        </InputWrapper>
      </Field>
    );
  }
);
